import { useMemo } from 'react'
import { Clip } from '../types'

interface MemoryConfig {
  // Future Memory-specific options
}

// Singleton storage for memory backend
const globalSubscribers = new Set<(clips: Clip[]) => void>()
const globalClips: Clip[] = []

/**
 * Hook-based Memory backend for clipboard operations
 * Provides temporary in-memory storage with singleton pattern for cross-instance sharing
 */
export const useMemoryBackend = (config: MemoryConfig = {}) => {
  try {
    return useMemo(() => {
      return {
        isAvailable: () => true, // Always available

        subscribeToClips: (callback: (clips: Clip[]) => void) => {
          // Initial callback with current clips
          callback(globalClips)

          // Add to global subscribers
          globalSubscribers.add(callback)

          // Return unsubscribe function
          return () => {
            globalSubscribers.delete(callback)
          }
        },

        saveClips: async (newClips: Clip[]): Promise<void> => {
          // Update global storage
          globalClips.length = 0
          globalClips.push(...newClips)

          // Notify all global subscribers
          globalSubscribers.forEach(callback => {
            callback(newClips)
          })
        },

        getName: () => 'MemoryBackend'
      }
    }, [])
  } catch (error) {
    // Log the error for debugging
    console.warn('[Clipboard] Memory backend failed to initialize:', error)

    // Return a backend that is always unavailable
    return {
      isAvailable: () => false,
      subscribeToClips: (callback: (clips: Clip[]) => void) => {
        // Return empty clips and no-op unsubscribe
        callback([])
        return () => {}
      },
      saveClips: async (clips: Clip[]): Promise<void> => {
        // Silently fail - this backend is not available
        console.warn('[Clipboard] Memory backend is unavailable, save operation ignored')
      },
      getName: () => 'MemoryBackend (Unavailable)'
    }
  }
}

export default useMemoryBackend
